/**
 * Lightweight pause overlay: Resume + Settings.
 */
export class PauseMenu {
  private readonly root: HTMLDivElement;
  private open = false;
  private onResume: (() => void) | null = null;
  private onControls: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.id = 'pause-menu';
    this.root.innerHTML = `
      <div class="pause-panel" role="dialog" aria-label="Paused">
        <h2>Paused</h2>
        <p class="pause-sub">Cave time freezes while you are here.</p>
        <div class="pause-actions">
          <button type="button" data-action="resume">Resume</button>
          <button type="button" data-action="controls" class="secondary">Settings</button>
        </div>
        <p class="pause-hint">Esc resume</p>
      </div>
    `;
    parent.appendChild(this.root);
    this.injectStyles();

    this.root.querySelector('[data-action="resume"]')?.addEventListener('click', () => {
      this.onResume?.();
    });
    this.root.querySelector('[data-action="controls"]')?.addEventListener('click', () => {
      this.onControls?.();
    });
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(handlers: { onResume: () => void; onControls: () => void }): void {
    this.onResume = handlers.onResume;
    this.onControls = handlers.onControls;
    this.open = true;
    this.root.classList.add('open');
  }

  hide(): void {
    this.open = false;
    this.onResume = null;
    this.onControls = null;
    this.root.classList.remove('open');
  }

  dispose(): void {
    this.root.remove();
  }

  private injectStyles(): void {
    if (document.getElementById('pause-menu-styles')) return;
    const style = document.createElement('style');
    style.id = 'pause-menu-styles';
    style.textContent = `
      #pause-menu {
        position: absolute;
        inset: 0;
        z-index: 52;
        display: none;
        place-items: center;
        pointer-events: none;
        background: rgba(6, 10, 16, 0.55);
      }
      #pause-menu.open {
        display: grid;
        pointer-events: auto;
      }
      #pause-menu .pause-panel {
        width: min(320px, calc(100vw - 36px));
        padding: 26px 22px 18px;
        border: 1px solid rgba(232, 238, 245, 0.14);
        background: rgba(14, 20, 28, 0.92);
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
        text-align: center;
      }
      #pause-menu h2 {
        margin: 0 0 6px;
        font-size: 22px;
        letter-spacing: 0.06em;
      }
      #pause-menu .pause-sub {
        margin: 0 0 18px;
        font-size: 12px;
        opacity: 0.65;
      }
      #pause-menu .pause-actions {
        display: grid;
        gap: 8px;
      }
      #pause-menu button {
        appearance: none;
        border: 1px solid rgba(232, 238, 245, 0.22);
        background: #2a4a62;
        color: #e8eef5;
        font: inherit;
        font-size: 14px;
        padding: 11px 14px;
        cursor: pointer;
      }
      #pause-menu button.secondary {
        background: rgba(42, 74, 98, 0.45);
      }
      #pause-menu button:hover {
        filter: brightness(1.08);
      }
      #pause-menu .pause-hint {
        margin: 12px 0 0;
        font-size: 11px;
        opacity: 0.45;
      }
    `;
    document.head.appendChild(style);
  }
}
